import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  hasNextPage: boolean;
  loading: boolean;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onGoToPage: (page: number) => void;
}

export function Pagination({
  currentPage,
  hasNextPage,
  loading,
  onPreviousPage,
  onNextPage,
  onGoToPage,
}: PaginationProps) {
  // Calculate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxPages = currentPage + (hasNextPage ? 2 : 0);
    
    // Always show first page
    pages.push(1);
    
    // Show ellipsis if current page is far from start
    if (currentPage > 3) {
      pages.push('...');
    }
    
    // Show pages around current page
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(maxPages, currentPage + 1); i++) {
      if (i !== 1 && i <= currentPage) {
        pages.push(i);
      }
    }
    
    // Show current page if not already shown
    if (!pages.includes(currentPage)) {
      pages.push(currentPage);
    }
    
    // Show next page if it exists
    if (hasNextPage && !pages.includes(currentPage + 1)) {
      pages.push(currentPage + 1);
    }
    
    // Show ellipsis if there might be more pages
    if (hasNextPage && currentPage < maxPages - 1) {
      if (!pages.includes('...')) {
        pages.push('...');
      }
    }
    
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex items-center justify-center gap-0 py-1">
      {/* Previous Button */}
      <Button
        onClick={onPreviousPage}
        disabled={currentPage === 1 || loading}
        variant="ghost"
        size="sm"
        className="h-8 w-10 p-0 hover:bg-white/50 border-0 rounded-none"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      {/* Page Numbers */}
      <div className="flex items-center gap-0">
        {pageNumbers.map((page, index) => {
          if (page === '...') {
            return (
              <span key={`ellipsis-${currentPage}-${index}`} className="px-3 text-gray-500 text-sm">
                ...
              </span>
            );
          }

          const pageNum = page as number;
          const isCurrentPage = pageNum === currentPage;

          return (
            <Button
              key={`page-${pageNum}`}
              onClick={() => onGoToPage(pageNum)}
              disabled={loading || pageNum > currentPage + 1}
              variant="ghost"
              size="sm"
              className={`h-8 min-w-10 px-3 text-sm hover:bg-white/50 border-0 rounded-none ${
                isCurrentPage
                  ? 'bg-white/50 text-gray-900'
                  : 'text-gray-500'
              }`}
            >
              {pageNum}
            </Button>
          );
        })}
      </div>

      {/* Next Button */}
      <Button
        onClick={onNextPage}
        disabled={!hasNextPage || loading}
        variant="ghost"
        size="sm"
        className="h-8 w-10 p-0 hover:bg-white/50 border-0 rounded-none"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

